import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Loader, IconButton, icons } from '@ublend-npm/aulaui-next';
import { useAulaEditorContext } from '../../EditorContext';
import { useAnalytics } from '../../lib/useAnalytics';
import { BlockElement } from '../../types';
import { LtiRequestType } from '../../Entities';
import { Lti1p3Form } from './Lti1p3Form';
import { lti1p3Launch } from './lti1p3Launch';
import {
  ErrorTitle,
  ErrorDescription,
  FormErrorBlock,
  StyledModal,
  IFrame,
  LoaderContainer,
  ContentContainer,
  ScreenReaderOnly,
  FormErrorMessage,
  ContentHeaderContainer,
  ContentHeader,
  HelpLink,
} from './DeepLinkingModal.styled';

export type DeepLinkingModalProps = {
  element: BlockElement;
  loginUrl: string;
  targetUrl: string;
  clientId: string;
  iss: string;
  provider: string;
  onCloseModal: (entityId: string) => void;
};

export enum DeepLinkingModalError {
  FORM_FAILED_TO_LOAD = 'form-failed-to-load',
  EMPTY_SELECTION = 'empty-selection',
  FORM_FAILED_TO_SUBMIT = 'form-failed-to-submit',
}

const errorTextMap: Record<
  DeepLinkingModalError,
  { title: string; text: string }
> = {
  [DeepLinkingModalError.FORM_FAILED_TO_LOAD]: {
    title: 'Loading error',
    text:
      'An error occurred during the loading of this form. Please try again later.',
  },
  [DeepLinkingModalError.EMPTY_SELECTION]: {
    title: '',
    text: 'Please select an option in Step 1 below before submitting the form',
  },
  [DeepLinkingModalError.FORM_FAILED_TO_SUBMIT]: {
    title: 'Form submit error',
    text:
      'An error occurred while this form was submitted. Please try again later.',
  },
};

const analyticsItemType = 'ltiIntegration';

export const DeepLinkingModal = ({
  loginUrl,
  targetUrl,
  clientId,
  iss,
  element,
  provider = 'Unknown',
  onCloseModal = () => {},
}: DeepLinkingModalProps) => {
  const iframeId = 'deep-linking-frame';
  const spinnerSize = 50;
  const centerOffset = `${spinnerSize / 2}px`;

  const {
    updateEntity,
    config: { spaceId, userId, apiUrl, sessionToken },
  } = useAulaEditorContext();
  const track = useAnalytics();
  const [loginHint, setLoginHint] = useState('');
  const [error, setError] = useState<DeepLinkingModalError | null>(null);
  const [hasIframeLoaded, setHasIframeLoaded] = useState(false);
  const [
    hasSentOpenDeepLinkingModalEvent,
    setHasSentOpenDeepLinkingModalEvent,
  ] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  const { entity: entityId } = element;

  const closeModal = () => {
    track('CLOSE_DEEPLINKING_MODAL', {
      itemType: 'ltiIntegration',
      provider,
    });
    onCloseModal(entityId);
  };

  const launchLti = useCallback(() => {
    lti1p3Launch({
      apiUrl,
      spaceId,
      clientId,
      type: LtiRequestType.DEEP_LINKING,
      sessionToken,
      userId,
    })
      .then((ltiSessionToken) => setLoginHint(ltiSessionToken))
      .catch(() => {
        setError(DeepLinkingModalError.FORM_FAILED_TO_LOAD);
      });
  }, [apiUrl, spaceId, clientId, sessionToken, userId]);

  useEffect(() => {
    launchLti();
  }, [launchLti]);

  useEffect(() => {
    if (!hasSentOpenDeepLinkingModalEvent) {
      setHasSentOpenDeepLinkingModalEvent(true);
      track('OPEN_DEEPLINKING_MODAL', {
        provider,
        itemType: analyticsItemType,
      });
    }
  }, [hasSentOpenDeepLinkingModalEvent, provider, track]);

  useEffect(() => {
    if (loginHint) {
      formRef.current?.requestSubmit();
    }
  }, [loginHint]);

  useEffect(() => {
    const handleOnMessage = ({ origin, data }: MessageEvent) => {
      // ignore messages coming from any source that is not Aula API
      if (origin !== apiUrl) {
        return;
      }

      if (typeof data === 'string' && data.startsWith('LTIDeepLinking:Error')) {
        setError(DeepLinkingModalError.FORM_FAILED_TO_SUBMIT);
        return;
      }

      if (
        typeof data === 'string' &&
        data.startsWith('LTIDeepLinking:Success')
      ) {
        const [_, contentItemJson] = data.split('|');
        const contentItems = JSON.parse(contentItemJson);
        updateEntity(entityId, { params: { contentItem: contentItems[0] } });
      } else if (
        typeof data === 'string' &&
        data.startsWith('LTIDeepLinking:EmptySelection')
      ) {
        setError(DeepLinkingModalError.EMPTY_SELECTION);
        launchLti();
      } else if (
        typeof data === 'string' &&
        data.startsWith('LTIDeepLinking:Cancel')
      ) {
        closeModal();
      }
    };
    window.addEventListener('message', handleOnMessage);
    return () => {
      window.removeEventListener('message', handleOnMessage);
    };
  }, [apiUrl, entityId, launchLti, updateEntity]);

  const onHelpLinkClickHandler = () => {
    track('DEEPLINKING_HELP', { provider, itemType: analyticsItemType });
  };

  return (
    <StyledModal
      open
      id="content-selection-modal"
      size="default"
      onClose={closeModal}
    >
      <ContentContainer>
        <ContentHeaderContainer>
          <ContentHeader>Add an integration</ContentHeader>
          <IconButton
            style={{ color: 'black' }}
            size={24}
            label="Close"
            icon={icons.Close}
            type="secondary"
            onClick={closeModal}
            data-testid="close-deep-link-modal-button"
          />
        </ContentHeaderContainer>
        <HelpLink>
          <span>For help with this integration visit </span>
          <a
            target="_blank"
            rel="noreferrer"
            onClick={onHelpLinkClickHandler}
            title="Open help link in new tab"
            href="https://aula.zendesk.com/hc/en-us/articles/360053178513-Using-External-Tools-and-Services-with-Aula"
          >
            <span>Aula&apos;s Help Centre</span>
            <icons.OpenInNew
              label="Open help link in new tab"
              fontSize="inherit"
            />
          </a>
        </HelpLink>
        {error &&
          (errorTextMap[error].title ? (
            <FormErrorBlock aria-live="assertive" aria-atomic="true">
              <ErrorTitle>{errorTextMap[error].title}</ErrorTitle>
              <ErrorDescription>{errorTextMap[error].text}</ErrorDescription>
            </FormErrorBlock>
          ) : (
            <FormErrorMessage>{errorTextMap[error].text}</FormErrorMessage>
          ))}
        <LoaderContainer centerOffset={centerOffset}>
          <Loader size={spinnerSize} />
        </LoaderContainer>
        <Lti1p3Form
          loginUrl={loginUrl}
          targetUrl={targetUrl}
          clientId={clientId}
          iss={iss}
          type={LtiRequestType.DEEP_LINKING}
          target={iframeId}
          formRef={formRef}
          loginHint={loginHint}
        />
        {(!error || error === DeepLinkingModalError.EMPTY_SELECTION) && (
          <IFrame
            onLoad={() => setHasIframeLoaded(true)}
            title="Deep linking content selector"
            id={iframeId}
            name={iframeId}
            allow="fullscreen"
            data-testid="lti-iframe"
          />
        )}
        {hasIframeLoaded && (
          <ScreenReaderOnly aria-live="polite">
            Engageli content has loaded please navigate using keyboard
          </ScreenReaderOnly>
        )}
      </ContentContainer>
    </StyledModal>
  );
};
